import React from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePlans } from '../contexts/PlanContext';

interface CategoryTransactionWarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  categoryName: string;
  categoryId: string;
  isParent?: boolean;
  transactionCount: number;
  monthPlanId?: string;
}

export const CategoryTransactionWarningModal: React.FC<CategoryTransactionWarningModalProps> = ({
  isOpen,
  onClose,
  categoryName,
  categoryId,
  isParent = false,
  transactionCount,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentMonthPlan } = usePlans();

  const handleViewTransactions = () => {
    // Navigate to history page with category filter and no date filter
    if (isParent) {
      navigate('/history', {
        state: {
          parentCategoryId: categoryId,
          monthPlanId: currentMonthPlan?.id,
        }
      });
    } else {
      navigate('/history', {
        state: {
          categoryId: categoryId,
          monthPlanId: currentMonthPlan?.id
          // No dateFrom or dateTo - this will show ALL transactions for this category
        }
      });
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white border-4 border-[#077f5d] max-w-md w-full"
        style={{ boxShadow: '4px 4px 0 #077f5d' }}
      >
        {/* Header */}
        <div className="bg-[#3cbe8c] border-b-4 border-[#077f5d] p-4 flex items-center justify-between">
          <h2
            className="text-white uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '12px',
              lineHeight: '1.4'
            }}
          >
            {t('transaction_warning_modal.title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-white hover:bg-[#077f5d]"
            style={{ border: '2px solid #077f5d' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            <X size={20} className="text-white" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <p className="font-mono text-sm text-[#1a1a1a]">
            {t('transaction_warning_modal.message_prefix')} <strong className="text-[#077f5d]">"{categoryName}"</strong> {t('transaction_warning_modal.message_has')}{' '}
            <strong className="text-[#3cbe8c]">{transactionCount} {t('transaction_warning_modal.message_transactions')}</strong>.
          </p>
          <p className="font-mono text-sm text-[#1a1a1a]">
            {t('transaction_warning_modal.message_suffix')}
          </p>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 p-6">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-3 bg-white text-[#077f5d] border-2 border-[#077f5d] font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '4px 4px 0 #077f5d'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '2px 2px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 #077f5d';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            {t('transaction_warning_modal.cancel')}
          </button>
          <button
            type="button"
            onClick={handleViewTransactions}
            className="flex-1 px-4 py-3 bg-blue-500 text-white border-2 border-blue-700 font-bold uppercase"
            style={{
              fontFamily: "'Press Start 2P', monospace",
              fontSize: '10px',
              boxShadow: '6px 6px 0 blue-800'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = '4px 4px 0 blue-800';
              e.currentTarget.style.transform = 'translate(-2px, -2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = '6px 6px 0 blue-800';
              e.currentTarget.style.transform = 'translate(0, 0)';
            }}
          >
            {t('transaction_warning_modal.view_transactions')}
          </button>
        </div>
      </div>
    </div>
  );
};
